import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { C, font } from "../theme";
import { Card } from "./Card";
import { Label } from "./Label";
import { Button } from "./Button";
import { FieldInput } from "./Field";
import { Select } from "./Select";
import { money } from "../lib/format";
import { useSession } from "../lib/session/SessionContext";
import { ApiError } from "../lib/api/client";
import {
  addItemToTab,
  closeTab,
  createQuickSale,
  getCurrentCashSession,
  getProduct,
  getTabDetails,
  listProducts,
  listTabs,
  openTab,
  removeItemFromTab,
  searchMembersAutocomplete,
} from "../lib/api/endpoints";
import { CASH_SESSION_STATUS, PAYMENT_METHOD, paymentMethodLabel } from "../lib/enums";
import type { CashSessionDto, MemberAutocompleteDto, ProductDto, SaleDto, SaleItemRequest } from "../types";

type Mode = "quick" | "tabs";

const SUCCESS_DISMISS_MS = 4000;

interface CartLine {
  productId: string;
  name: string;
  unitPrice: number;
  quantity: number;
}

const PAYMENT_OPTIONS = [
  { value: String(PAYMENT_METHOD.Cash), label: paymentMethodLabel(PAYMENT_METHOD.Cash) },
  { value: String(PAYMENT_METHOD.Pix), label: paymentMethodLabel(PAYMENT_METHOD.Pix) },
  { value: String(PAYMENT_METHOD.CreditCard), label: paymentMethodLabel(PAYMENT_METHOD.CreditCard) },
  { value: String(PAYMENT_METHOD.DebitCard), label: paymentMethodLabel(PAYMENT_METHOD.DebitCard) },
];

function ProductImage({ name, imageUrl, height }: { name: string; imageUrl?: string | null; height: number }) {
  if (!imageUrl) {
    return (
      <div
        style={{
          height,
          borderRadius: 8,
          background: C.faint,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: C.light,
          fontSize: height * 0.32,
          fontWeight: 700,
        }}
      >
        {name.charAt(0).toUpperCase()}
      </div>
    );
  }
  return <img src={imageUrl} alt={name} style={{ height, width: "100%", objectFit: "cover", borderRadius: 8, display: "block" }} />;
}

export function PdvTab() {
  const { accessToken } = useSession();
  const token = accessToken ?? "";

  const [mode, setMode] = useState<Mode>("quick");
  const [session, setSession] = useState<CashSessionDto | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [stockWarning, setStockWarning] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState<ProductDto[] | null>(null);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [quickError, setQuickError] = useState<string | null>(null);

  const [tabs, setTabs] = useState<SaleDto[] | null>(null);
  const [selectedTab, setSelectedTab] = useState<SaleDto | null>(null);
  const [tabCustomer, setTabCustomer] = useState("");
  const [tabMember, setTabMember] = useState<MemberAutocompleteDto | null>(null);
  const [memberResults, setMemberResults] = useState<MemberAutocompleteDto[] | null>(null);
  const [memberSearching, setMemberSearching] = useState(false);
  const [tabError, setTabError] = useState<string | null>(null);
  const [openTabForm, setOpenTabForm] = useState(false);
  const [addItemForm, setAddItemForm] = useState(false);
  const [closingTab, setClosingTab] = useState(false);
  const [tabPayment, setTabPayment] = useState<string>("");
  const [tabCloseError, setTabCloseError] = useState<string | null>(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  const cashOpen = session?.status === CASH_SESSION_STATUS.Open;

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(null), SUCCESS_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    getCurrentCashSession(token)
      .then(s => {
        if (!cancelled) setSession(s);
      })
      .catch(e => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.detail ?? e.message : "Falha ao verificar o caixa");
      });
    return () => {
      cancelled = true;
    };
  }, [token, refreshKey]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setError(null);
    listProducts(token, { page: 1, pageSize: 20, search })
      .then(r => {
        if (!cancelled) setProducts(r.items);
      })
      .catch(e => {
        if (cancelled) return;
        setProducts([]);
        setError(e instanceof ApiError ? e.detail ?? e.message : "Falha ao carregar produtos");
      });
    return () => {
      cancelled = true;
    };
  }, [token, search]);

  useEffect(() => {
    if (!token || mode !== "tabs") return;
    let cancelled = false;
    listTabs(token, { status: 1 })
      .then(r => {
        if (!cancelled) setTabs(r.items);
      })
      .catch(e => {
        if (cancelled) return;
        setTabs([]);
        setError(e instanceof ApiError ? e.detail ?? e.message : "Falha ao carregar comandas");
      });
    return () => {
      cancelled = true;
    };
  }, [token, mode, refreshKey]);

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    if (!token || mode !== "tabs") return;
    const term = tabCustomer.trim();
    if (term.length < 2 || (tabMember && tabMember.name === term)) {
      setMemberResults(null);
      return;
    }
    let cancelled = false;
    const timeout = setTimeout(() => {
      setMemberSearching(true);
      searchMembersAutocomplete(token, { q: term, limit: 8 })
        .then(r => { if (!cancelled) setMemberResults(r); })
        .catch(() => { if (!cancelled) setMemberResults([]); })
        .finally(() => { if (!cancelled) setMemberSearching(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [token, mode, tabCustomer, tabMember]);

  const selectTabMember = (member: MemberAutocompleteDto) => {
    setTabMember(member);
    setTabCustomer(member.name);
    setMemberResults(null);
    setTabError(null);
  };

  const cartTotal = useMemo(() => cart.reduce((acc, l) => acc + l.unitPrice * l.quantity, 0), [cart]);

  const addToCart = (p: ProductDto) => {
    setQuickError(null);
    setCart(lines => {
      const existing = lines.find(l => l.productId === p.id);
      if (existing) {
        return lines.map(l => (l.productId === p.id ? { ...l, quantity: l.quantity + 1 } : l));
      }
      return [...lines, { productId: p.id, name: p.name, unitPrice: p.price, quantity: 1 }];
    });
  };

  const changeQty = (productId: string, quantity: number) => {
    setCart(lines =>
      quantity <= 0
        ? lines.filter(l => l.productId !== productId)
        : lines.map(l => (l.productId === productId ? { ...l, quantity } : l)),
    );
  };

  const handleQuickSale = async (e: FormEvent) => {
    e.preventDefault();
    if (!cashOpen) {
      setQuickError("Abra o caixa para vender");
      return;
    }
    if (cart.length === 0) {
      setQuickError("Adicione ao menos um produto");
      return;
    }
    const method = Number(paymentMethod);
    if (!paymentMethod || !PAYMENT_OPTIONS.some(o => o.value === paymentMethod)) {
      setQuickError("Selecione a forma de pagamento");
      return;
    }
    const items: SaleItemRequest[] = cart.map(l => ({ productId: l.productId, quantity: l.quantity }));
    setSubmitting(true);
    setSubmitError(null);
    setQuickError(null);
    setSuccess(null);
    try {
      await createQuickSale(token, { items, paymentMethod: method });
      setCart([]);
      setPaymentMethod("");
      setSuccess("Venda finalizada");
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.detail ?? err.message : "Falha ao finalizar venda");
    } finally {
      setSubmitting(false);
    }
  };

  const loadTabDetails = async (saleId: string) => {
    try {
      const details = await getTabDetails(token, saleId);
      setSelectedTab(details.sale);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail ?? err.message : "Falha ao carregar comanda");
    }
  };

  const handleOpenTab = async (e: FormEvent) => {
    e.preventDefault();
    if (!cashOpen) {
      setTabError("Abra o caixa para vender");
      return;
    }
    const customerName = tabCustomer.trim();
    if (customerName.length > 120) {
      setTabError("Cliente deve ter no máximo 120 caracteres");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setTabError(null);
    try {
      const sale = await openTab(token, { customerId: tabMember?.id ?? null, customerName: customerName || null });
      setOpenTabForm(false);
      setTabCustomer("");
      setTabMember(null);
      setMemberResults(null);
      setSelectedTab(sale);
      reload();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.detail ?? err.message : "Falha ao abrir comanda");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddItem = async (product: ProductDto) => {
    if (!selectedTab || !cashOpen) return;
    if (product.stockQuantity <= 0) {
      setStockWarning(`Estoque insuficiente para ${product.name}. O item será incluído e a divergência ficará registrada.`);
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const sale = await addItemToTab(token, selectedTab.id, { productId: product.id, quantity: 1 });
      setSelectedTab(sale);
      try {
        const refreshedProduct = await getProduct(token, product.id);
        setProducts(current => current?.map(item => item.id === refreshedProduct.id ? refreshedProduct : item) ?? null);
        if (refreshedProduct.stockQuantity < 0) {
          setStockWarning(`Estoque insuficiente para ${refreshedProduct.name}. Item incluído; saldo atual: ${refreshedProduct.stockQuantity}.`);
        }
      } catch {
        setSubmitError("Item adicionado, mas não foi possível atualizar o saldo de estoque.");
      }
      reload();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.detail ?? err.message : "Falha ao adicionar item");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!selectedTab || !cashOpen) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const sale = await removeItemFromTab(token, selectedTab.id, itemId);
      setSelectedTab(sale);
      reload();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.detail ?? err.message : "Falha ao remover item");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseTab = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedTab) return;
    const method = Number(tabPayment);
    if (!tabPayment || !PAYMENT_OPTIONS.some(o => o.value === tabPayment)) {
      setTabCloseError("Selecione a forma de pagamento");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setTabCloseError(null);
    try {
      await closeTab(token, selectedTab.id, { paymentMethod: method });
      setClosingTab(false);
      setTabPayment("");
      setSelectedTab(null);
      setSuccess("Comanda fechada");
      reload();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.detail ?? err.message : "Falha ao fechar comanda");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {session !== undefined && !cashOpen && (
        <Card style={{ borderColor: C.lime, background: C.limeSoft, marginBottom: 12 }}>
          <Label style={{ color: C.gray }}>⚠ Caixa fechado</Label>
          <p style={{ fontFamily: font, fontSize: 13, color: C.gray, margin: 0, lineHeight: 1.5 }}>
            <b style={{ color: C.ink }}>Abra o caixa para vender.</b> Sem uma sessão de caixa aberta, as vendas não entram no fluxo
            financeiro do balcão.
          </p>
        </Card>
      )}

      <Card>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <Label style={{ margin: 0 }}>Balcão</Label>
          <div style={{ display: "inline-flex", gap: 8 }}>
            <Button variant={mode === "quick" ? "dark" : "outline"} onClick={() => setMode("quick")} disabled={submitting} style={{ fontSize: 12 }}>
              Venda rápida
            </Button>
            <Button variant={mode === "tabs" ? "dark" : "outline"} onClick={() => { setMode("tabs"); setSelectedTab(null); }} disabled={submitting} style={{ fontSize: 12 }}>
              Comandas
            </Button>
          </div>
        </div>

        {error && (
          <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "C.redSoft", color: C.red, fontSize: 12.5 }}>{error}</div>
        )}
        {submitError && (
          <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "C.redSoft", color: C.red, fontSize: 12.5 }}>{submitError}</div>
        )}
        {stockWarning && (
          <div role="alert" style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: C.redSoft, color: C.red, fontSize: 12.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <span>⚠ {stockWarning}</span>
            <button type="button" aria-label="Fechar alerta de estoque" onClick={() => setStockWarning(null)} style={{ background: "none", border: "none", cursor: "pointer", color: C.red, fontSize: 14, padding: 2 }}>✕</button>
          </div>
        )}
        {success && (
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              borderRadius: 8,
              background: C.limeSoft,
              color: C.ink,
              fontSize: 12.5,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <span>✓ {success}</span>
            <button
              type="button"
              aria-label="Fechar aviso"
              onClick={() => setSuccess(null)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: C.gray,
                fontSize: 14,
                lineHeight: 1,
                padding: 2,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {mode === "quick" && (
          <form onSubmit={handleQuickSale} noValidate style={{ marginTop: 12 }}>
            <div style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <FieldInput
                  placeholder="Buscar produto"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  disabled={!cashOpen}
                  style={{ maxWidth: 320 }}
                />

                {products === null ? (
                  <div style={{ color: C.light, fontSize: 13, padding: "14px 4px" }}>Carregando produtos...</div>
                ) : products.length === 0 ? (
                  <div style={{ color: C.light, fontSize: 13, padding: "14px 4px" }}>Nenhum produto encontrado.</div>
                ) : (
                  <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 10, maxHeight: 380, overflowY: "auto", padding: 2 }}>
                    {products.map(p => (
                      <div key={p.id} style={{ display: "flex", flexDirection: "column", gap: 8, border: `1px solid ${C.faint}`, borderRadius: 12, padding: 12, background: C.bg }}>
                        <ProductImage name={p.name} imageUrl={p.imageUrl} height={56} />
                        <div>
                          <div style={{ fontWeight: 600, color: C.ink, fontSize: 13, lineHeight: 1.3 }}>{p.name}</div>
                          <div style={{ color: C.gray, fontSize: 12, marginTop: 2 }}>{money(p.price)}</div>
                          <div style={{ color: p.stockQuantity <= 0 ? C.red : C.light, fontSize: 11, marginTop: 2 }}>Saldo: {p.stockQuantity}</div>
                        </div>
                        <Button variant="outline" onClick={() => addToCart(p)} disabled={!cashOpen || submitting} ariaLabel={`Adicionar ${p.name}`} style={{ fontSize: 11.5, padding: "5px 11px", width: "100%" }}>
                          Adicionar
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div
                style={{
                  width: 320,
                  flexShrink: 0,
                  height: 440,
                  display: "flex",
                  flexDirection: "column",
                  background: C.bg,
                  border: `1px solid ${C.faint}`,
                  borderRadius: 14,
                  padding: 14,
                }}
              >
                <div style={{ fontWeight: 700, color: C.ink, fontSize: 13, letterSpacing: 0.3 }}>Carrinho</div>

                {cart.length === 0 ? (
                  <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: C.light, fontSize: 13 }}>
                    Carrinho vazio.
                  </div>
                ) : (
                  <>
                    <div style={{ flex: 1, overflowY: "auto", marginTop: 6 }}>
                      {cart.map(l => (
                        <div key={l.productId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 0", borderBottom: `1px solid ${C.faint}`, fontSize: 13 }}>
                          <span style={{ flex: 1, fontWeight: 600, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</span>
                          <input
                            type="number"
                            min={1}
                            value={l.quantity}
                            onChange={e => changeQty(l.productId, Number(e.target.value))}
                            disabled={!cashOpen || submitting}
                            aria-label={`Quantidade ${l.name}`}
                            style={{ width: 52, fontFamily: font, fontSize: 13, color: C.ink, border: `1px solid ${C.faint}`, borderRadius: 8, padding: 5, textAlign: "center" }}
                          />
                          <span style={{ width: 72, textAlign: "right", fontWeight: 700, color: C.ink }}>{money(l.unitPrice * l.quantity)}</span>
                          <Button
                            variant="ghost"
                            onClick={() => changeQty(l.productId, 0)}
                            disabled={!cashOpen || submitting}
                            ariaLabel={`Remover ${l.name}`}
                            style={{ textDecoration: "none", color: C.red }}
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0 10px", borderTop: `1px solid ${C.faint}`, fontSize: 15 }}>
                      <span style={{ fontWeight: 700, color: C.ink }}>Total</span>
                      <span style={{ flex: 1 }} />
                      <span style={{ fontWeight: 700, color: C.ink, fontSize: 17 }}>{money(cartTotal)}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div>
                        <label htmlFor="quick-payment" style={{ display: "block", fontFamily: font, fontSize: 12, fontWeight: 600, color: C.gray, marginBottom: 4 }}>
                          Forma de pagamento *
                        </label>
                        <Select id="quick-payment" value={paymentMethod} options={PAYMENT_OPTIONS} placeholder="Selecione..." disabled={!cashOpen || submitting} onChange={setPaymentMethod} style={{ width: "100%" }} />
                      </div>
                      <Button type="submit" disabled={!cashOpen || submitting} ariaLabel="Finalizar venda">
                        {submitting ? "Finalizando..." : "Finalizar venda"}
                      </Button>
                      {quickError && <div style={{ color: C.red, fontSize: 11.5 }}>{quickError}</div>}
                    </div>
                  </>
                )}
              </div>
            </div>
          </form>
        )}

        {mode === "tabs" && (
          <div style={{ marginTop: 12 }}>
            {tabs === null ? (
              <div style={{ color: C.light, fontSize: 13, padding: "14px 4px" }}>Carregando comandas...</div>
            ) : (
              <>
                {!selectedTab && (
                  <>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12.5, color: C.gray }}>
                        {tabs.length} comanda(s) aberta(s)
                      </span>
                      {!openTabForm && (
                        <Button variant="outline" onClick={() => { setOpenTabForm(true); setTabError(null); }} disabled={!cashOpen || submitting} style={{ fontSize: 12 }}>
                          Abrir comanda
                        </Button>
                      )}
                    </div>

                    {openTabForm && (
                      <form onSubmit={handleOpenTab} noValidate style={{ marginTop: 12, padding: 12, background: C.bg, borderRadius: 10 }}>
                        <label htmlFor="tab-customer" style={{ display: "block", fontFamily: font, fontSize: 12, fontWeight: 600, color: C.gray, marginBottom: 4 }}>
                          Cliente
                        </label>
                        <div style={{ position: "relative", maxWidth: 280 }}>
                          <FieldInput id="tab-customer" value={tabCustomer} onChange={e => { setTabCustomer(e.target.value); setTabMember(null); }} placeholder="Nome ou matrícula (opcional)" />
                          {memberSearching && <div style={{ color: C.light, fontSize: 11.5, marginTop: 6 }}>Buscando alunos...</div>}
                          {memberResults !== null && memberResults.length === 0 && !memberSearching && <div style={{ color: C.light, fontSize: 11.5, marginTop: 6 }}>Nenhum aluno encontrado.</div>}
                          {memberResults !== null && memberResults.length > 0 && (
                            <div role="listbox" aria-label="Alunos encontrados" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, width: "100%", minWidth: 300, zIndex: 30, background: C.white, border: `1px solid ${C.faint}`, borderRadius: 10, boxShadow: "0 10px 26px rgba(17,17,17,0.12)", padding: 4 }}>
                              {memberResults.map(member => (
                                <button
                                  type="button"
                                  key={member.id}
                                  role="option"
                                  onClick={() => selectTabMember(member)}
                                  style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, width: "100%", fontFamily: font, fontSize: 12.5, color: C.ink, background: "transparent", border: "none", borderRadius: 8, padding: "8px 10px", cursor: "pointer", textAlign: "left" }}
                                  onMouseEnter={e => (e.currentTarget.style.background = C.limeSoft)}
                                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                                >
                                  <span style={{ fontWeight: 600 }}>{member.name}</span>
                                  <span style={{ color: C.gray, fontSize: 11 }}>{member.registrationNumber} · {member.document}{member.phone ? ` · ${member.phone}` : ""}</span>
                                  {member.hasOpenTab && <span style={{ color: C.red, fontSize: 10.5, fontWeight: 700 }}>Comanda aberta</span>}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {tabMember?.hasOpenTab && <div role="alert" style={{ color: C.red, fontSize: 11.5, marginTop: 6 }}>Este aluno já possui comanda aberta.</div>}
                        {tabError && <div style={{ color: C.red, fontSize: 11.5, marginTop: 6 }}>{tabError}</div>}
                        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                          <Button type="submit" disabled={submitting} style={{ fontSize: 12 }}>
                            {submitting ? "Criando..." : "Criar comanda"}
                          </Button>
                          <Button variant="outline" onClick={() => setOpenTabForm(false)} disabled={submitting} style={{ fontSize: 12 }}>
                            Cancelar
                          </Button>
                        </div>
                      </form>
                    )}

                    {tabs.length === 0 ? (
                      <div style={{ color: C.light, fontSize: 13, padding: "18px 4px" }}>Nenhuma comanda aberta.</div>
                    ) : (
                      <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 10 }}>
                        {tabs.map(t => (
                          <div key={t.id} style={{ display: "flex", flexDirection: "column", gap: 8, border: `1px solid ${C.faint}`, borderRadius: 12, padding: 12, background: C.bg }}>
                            <div style={{ fontWeight: 600, color: C.ink, fontSize: 13 }}>{t.customerName ?? `Comanda ${t.id.slice(0, 8)}`}</div>
                            <div style={{ fontSize: 11, color: C.light }}>{money(t.totalAmount)}</div>
                            <Button variant="outline" onClick={() => loadTabDetails(t.id)} disabled={submitting} style={{ fontSize: 11.5, padding: "5px 11px", width: "100%" }}>
                              Abrir
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {selectedTab && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Button variant="ghost" onClick={() => setSelectedTab(null)} disabled={submitting} ariaLabel="Voltar para comandas" style={{ textDecoration: "none", padding: 0, fontSize: 12, fontWeight: 600, color: C.gray }}>
                        ← Voltar
                      </Button>
                      <Label style={{ margin: 0, color: C.ink }}>{selectedTab.customerName ?? "Comanda"}</Label>
                      <div style={{ flex: 1 }} />
                      <span style={{ fontWeight: 700, color: C.ink, fontSize: 15 }}>{money(selectedTab.totalAmount)}</span>
                    </div>

                    {!addItemForm && (
                      <Button variant="outline" onClick={() => setAddItemForm(true)} disabled={!cashOpen || submitting} style={{ fontSize: 12, marginTop: 12 }}>
                        Adicionar item
                      </Button>
                    )}

                    {addItemForm && (
                      <div style={{ marginTop: 10, padding: 12, background: C.bg, borderRadius: 10 }}>
                        <FieldInput placeholder="Buscar produto" value={searchInput} onChange={e => setSearchInput(e.target.value)} style={{ maxWidth: 320 }} />
                        {products === null ? (
                          <div style={{ color: C.light, fontSize: 13, padding: "10px 4px" }}>Carregando produtos...</div>
                        ) : (
                          <div style={{ marginTop: 6, display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 10, maxHeight: 200, overflowY: "auto", padding: 2 }}>
                            {products.map(p => (
                              <div key={p.id} style={{ display: "flex", flexDirection: "column", gap: 8, border: `1px solid ${C.faint}`, borderRadius: 12, padding: 12, background: C.white }}>
                                <ProductImage name={p.name} imageUrl={p.imageUrl} height={48} />
                                <div>
                                  <div style={{ fontWeight: 600, color: C.ink, fontSize: 13, lineHeight: 1.3 }}>{p.name}</div>
                                  <div style={{ color: C.gray, fontSize: 12, marginTop: 2 }}>{money(p.price)}</div>
                                  <div style={{ color: p.stockQuantity <= 0 ? C.red : C.light, fontSize: 11, marginTop: 2 }}>Saldo: {p.stockQuantity}</div>
                                </div>
                                <Button variant="outline" onClick={() => handleAddItem(p)} disabled={!cashOpen || submitting} ariaLabel={`Adicionar ${p.name}`} style={{ fontSize: 11.5, padding: "5px 11px", width: "100%" }}>
                                  Adicionar
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        <Button variant="ghost" onClick={() => setAddItemForm(false)} disabled={submitting} style={{ textDecoration: "none", fontSize: 12, marginTop: 8 }}>
                          Fechar
                        </Button>
                      </div>
                    )}

                    {selectedTab.items && selectedTab.items.length === 0 ? (
                      <div style={{ color: C.light, fontSize: 13, padding: "14px 4px" }}>Comanda sem itens.</div>
                    ) : (
                      <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 10 }}>
                        {selectedTab.items?.map(item => {
                          const productName = products?.find(p => p.id === item.productId)?.name ?? item.productId.slice(0, 8);
                          return (
                            <div key={item.id} style={{ display: "flex", flexDirection: "column", gap: 8, border: `1px solid ${C.faint}`, borderRadius: 12, padding: 12, background: C.bg }}>
                              <div style={{ fontWeight: 600, color: C.ink, fontSize: 13, lineHeight: 1.3 }}>{productName}</div>
                              <div style={{ color: C.gray, fontSize: 12 }}>{item.quantity}x · {money(item.unitPrice)}</div>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <span style={{ fontWeight: 700, color: C.ink }}>{money(item.subtotal)}</span>
                                <Button
                                  variant="ghost"
                                  onClick={() => handleRemoveItem(item.id)}
                                  disabled={!cashOpen || submitting}
                                  ariaLabel="Remover item"
                                  style={{ textDecoration: "none", color: C.red }}
                                >
                                  ✕
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {!closingTab && (
                      <Button variant="outline" onClick={() => { setClosingTab(true); setTabCloseError(null); }} disabled={!cashOpen || submitting} style={{ fontSize: 12, marginTop: 12 }}>
                        Fechar comanda
                      </Button>
                    )}

                    {closingTab && (
                      <form onSubmit={handleCloseTab} noValidate style={{ marginTop: 12, padding: 12, background: C.bg, borderRadius: 10 }}>
                        <label htmlFor="tab-close-payment" style={{ display: "block", fontFamily: font, fontSize: 12, fontWeight: 600, color: C.gray, marginBottom: 4 }}>
                          Forma de pagamento *
                        </label>
                        <Select id="tab-close-payment" value={tabPayment} options={PAYMENT_OPTIONS} placeholder="Selecione..." disabled={submitting} onChange={setTabPayment} style={{ maxWidth: 260 }} />
                        {tabCloseError && <div style={{ color: C.red, fontSize: 11.5, marginTop: 6 }}>{tabCloseError}</div>}
                        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                          <Button type="submit" disabled={submitting} style={{ fontSize: 12 }}>
                            {submitting ? "Fechando..." : "Confirmar"}
                          </Button>
                          <Button variant="outline" onClick={() => setClosingTab(false)} disabled={submitting} style={{ fontSize: 12 }}>
                            Cancelar
                          </Button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Card>
    </>
  );
}
